import React, { forwardRef } from 'react';
import { Form } from 'react-bootstrap';
import { AsyncTypeahead, Hint, Menu, MenuItem } from 'react-bootstrap-typeahead';

import PropsType from "prop-types";

import './Types.css';

/* The column header sits outside the scrolling row list, so it has to reserve
   the same width the scrollbar takes from the rows - otherwise every column is
   off by the scrollbar width. Measured once; 0 on platforms with overlay
   scrollbars, which is also correct there. */
/**
 * Green or red for the stock column.
 *
 * The value arrives already worded - CustomerDashboard maps it to YES / NO -
 * so the colour is read from the word. Numbers still follow the original
 * rule: red only when the value parses to 0. (The original tested
 * `parseFloat(stock) === 0`, which coloured the string "NO" green, since
 * `NaN === 0` is false.)
 */
const NOT_IN_STOCK = ["no", "n", "false", "nil", "none", "out", "out of stock"];

const isInStock = (value) => {
    const qty = parseFloat(value);
    if (Number.isFinite(qty)) return qty !== 0;

    const text = String(value === null || value === undefined ? "" : value)
        .trim()
        .toLowerCase();
    return !NOT_IN_STOCK.includes(text);
};

const SCROLLBAR_WIDTH = (() => {
    if (typeof document === 'undefined') return 0;
    try {
        const probe = document.createElement('div');
        probe.style.cssText =
            'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll';
        document.body.appendChild(probe);
        const width = probe.offsetWidth - probe.clientWidth;
        document.body.removeChild(probe);
        return width;
    } catch (e) {
        return 0;
    }
})();

const Typeahead = forwardRef((propsFull, ref,) => {
    const filterBy = () => true;

    const { tabIndex, colWidth,
        isMobile,
        columns,
        labelKey,
        hasError,
        className,
        stock,
        showStock,
        width,
        noPadding,
        // optional extra cell at the end of each row, e.g. the "same content"
        // reload button on the Order screen. rowAction(result) -> node|null.
        // Clicks inside it never select the row.
        rowAction,
        rowActionHeader,
        ...props } = propsFull;
    //  console.log(showStock)

    return (
        <AsyncTypeahead
            //style={{ overflow: 'hidden' }}
            {...props}
            // `className` is pulled out of the props above, so props.className
            // was always undefined - every box got class "undefined search" and
            // none of the .searchProduct styles (border, icon, clear button)
            // ever applied.
            className={(className ? className + " " : "") + "search"}
            ref={ref}
            clearButton
            renderInput={({ inputRef, referenceElementRef, ...inputPropsFirst }) => {
                // const { newSelectionPrefix, ...inputProps } = inputPropsFirst;
                // console.log({ inputPropsFirst });

                return (
                    <Hint

                        shouldSelect={(shouldSelect, e) => {
                            //         // Select the hint if the user hits 'enter' or ','
                            return e.keyCode === 13 || shouldSelect;
                        }}
                    >
                        <Form.Control
                            {...inputPropsFirst}
                            style={{ ...inputPropsFirst.style, position: "inherit" }}
                            autoComplete="none"
                            tabIndex={tabIndex}
                            className="form-control "
                            ref={(element) => {
                                inputRef(element);
                                referenceElementRef(element);
                            }}
                        />
                    </Hint>
                );
            }}
            renderMenu={(results, menuPropsss) => {

                // console.log({ menuProps });

                if (!columns || columns.length === 0) {
                    return (
                        <Menu {...menuPropsss}  >
                            {results.map((result, index) => (
                                <MenuItem key={index} option={result} position={index}>
                                    {props.name ? result[props.name] : result.label}
                                </MenuItem>
                            ))}
                        </Menu>
                    );

                }

                // if (results.length === 0) {
                //     return <></>
                // }
                return (
                    <Menu
                        {...menuPropsss}
                        className={menuPropsss.className + " taMenu"}
                        style={{ ...menuPropsss.style }}
                    >
                        {/* One layout for every width. This used to branch on
                            window.innerWidth at render, which never re-ran on
                            resize, and rendering both layouts is not an option
                            here - duplicate MenuItems would break the keyboard
                            `position` indices. So the columns collapse to
                            label/value pairs by CSS below 576px instead. */}
                        <div
                            className={"taHead" + (rowAction ? " taHasAction" : "")}
                            aria-hidden="true"
                            style={{ paddingRight: SCROLLBAR_WIDTH }}
                        >
                            {columns.map((data, i) => (
                                <div
                                    key={i}
                                    className={`taCell col-${data.cols ? data.cols : 0}`}
                                    data-col={data.field}
                                >
                                    {data.header}
                                </div>
                            ))}
                            {rowAction ? (
                                <div className="taCell taActionCell">
                                    {rowActionHeader || ""}
                                </div>
                            ) : null}
                        </div>

                        <div className="taBody">
                            {results.map((result, index) => (
                                <MenuItem
                                    className="taItem"
                                    option={result}
                                    key={index}
                                    position={index}
                                >
                                    <div className={"taRow" + (rowAction ? " taHasAction" : "")}>
                                        {columns.map((data, i) => {
                                            const isStock = data.field === "stock";
                                            const inStock = isInStock(result.stock);
                                            return (
                                                <div
                                                    key={i}
                                                    className={
                                                        `taCell col-${data.cols ? data.cols : 0}` +
                                                        (isStock
                                                            ? " taStockCell " +
                                                              (inStock
                                                                  ? "taStockIn"
                                                                  : "taStockOut")
                                                            : "")
                                                    }
                                                    data-label={data.header}
                                                    data-col={data.field}
                                                >
                                                    {result[data.field]}
                                                </div>
                                            );
                                        })}
                                        {rowAction ? (
                                            <div
                                                className="taCell taActionCell"
                                                data-label={rowActionHeader || ""}
                                                // a click here must not pick the row
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                            >
                                                {rowAction(result)}
                                            </div>
                                        ) : null}
                                    </div>
                                </MenuItem>
                            ))}
                        </div>
                    </Menu>
                );
            }}
            filterBy={filterBy}
            minLength={2}
            labelKey={labelKey ? labelKey : "name"}
            inputProps={{
                autoComplete: "none",

                className:
                    "  " + (hasError ? "is-invalid" : "") + "  " + className
                        ? className
                        : "",
            }}
            multiple={false}
            autocomplete="none"
            useCache={false}

        />
    );
});


// https://reactjs.org/docs/typechecking-with-proptypes.html
Typeahead.propTypes = {
    name: PropsType.string,
    placeholder: PropsType.string,
    colWidth: PropsType.oneOfType([
        PropsType.string,
        PropsType.number
    ]),
    isMobile: PropsType.bool,
    tabIndex: PropsType.number,
    id: PropsType.string,
    columns: PropsType.array,
    isLoading: PropsType.bool,
    options: PropsType.array,
    selected: PropsType.array,
    onSearch: PropsType.func,
    onChange: PropsType.func,


}



export default Typeahead;


